/**
 * ADM-621 - bornes d'exercice comptable à partir d'une date de clôture.
 *
 * Miroir front de `Accounting::FiscalYear` (yeati-api) : l'exercice N se
 * termine le jour de clôture de l'année N et démarre le lendemain de la clôture
 * de N-1, donc sans chevauchement. Une clôture au 31/12 redonne exactement
 * l'exercice calendaire, ce qui reste le comportement par défaut.
 *
 * Toute divergence avec le back se traduirait par un budget lu sur le mauvais
 * millésime : les deux implémentations doivent rester alignées.
 */

export type FiscalYearConfig = {
  closingDay: number
  closingMonth: number
};

export const CALENDAR_FISCAL_YEAR: FiscalYearConfig = { closingDay: 31, closingMonth: 12 };

const toIso = (year: number, month: number, day: number) =>
  `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

const lastDayOfMonth = (year: number, month: number) => new Date(year, month, 0).getDate();

export const isCalendarFiscalYear = (config: FiscalYearConfig | null | undefined) =>
  !config || (config.closingDay === 31 && config.closingMonth === 12);

/**
 * Fin de l'exercice. Un jour qui n'existe pas dans le mois visé (29/02 une
 * année non bissextile, 31/06) est ramené au dernier jour réel du mois.
 */
export const fiscalYearEnd = (exercice: number, config?: FiscalYearConfig | null): string => {
  const { closingDay, closingMonth } = config ?? CALENDAR_FISCAL_YEAR;
  const day = Math.min(closingDay, lastDayOfMonth(exercice, closingMonth));
  return toIso(exercice, closingMonth, day);
};

/** Début de l'exercice = lendemain de la clôture précédente. */
export const fiscalYearStart = (exercice: number, config?: FiscalYearConfig | null): string => {
  const previousEnd = fiscalYearEnd(exercice - 1, config);
  const [year, month, day] = previousEnd.split("-").map(Number);
  const next = new Date(year, month - 1, day + 1);
  return toIso(next.getFullYear(), next.getMonth() + 1, next.getDate());
};

export const fiscalYearRange = (exercice: number, config?: FiscalYearConfig | null) => ({
  from: fiscalYearStart(exercice, config),
  to: fiscalYearEnd(exercice, config),
});

/** Millésime auquel appartient une date ISO (`YYYY-MM-DD`). */
export const fiscalExerciceForDate = (isoDate: string, config?: FiscalYearConfig | null): number => {
  const year = Number(isoDate.slice(0, 4));
  return isoDate <= fiscalYearEnd(year, config) ? year : year + 1;
};

export const formatFrDate = (isoDate: string) => {
  const [year, month, day] = isoDate.split("-");
  return `${day}/${month}/${year}`;
};

/** « Exercice 2026 » en calendaire, bornes explicitées sinon. */
export const fiscalYearLabel = (exercice: number, config?: FiscalYearConfig | null): string => {
  if (isCalendarFiscalYear(config)) return `Exercice ${exercice}`;
  const { from, to } = fiscalYearRange(exercice, config);
  return `Exercice ${exercice} (${formatFrDate(from)} → ${formatFrDate(to)})`;
};
